import { formatProvinceCodeForDisplay } from '@/lib/functions/formatProvinceCode';
import { DEFAULT_COUNTRY_CODE, getCountryLabel, isUnitedStates } from '@/lib/constants/countries';

import type { Address } from '@/lib/types/address';
import type { OrderPayload } from '@/lib/types/orderPayload';

const ESTIMATE_PLACEHOLDER_FIRST_NAME = 'Guest';
const ESTIMATE_PLACEHOLDER_LAST_NAME = 'Customer';
const ESTIMATE_PLACEHOLDER_PHONE = '5555555555';

type ShippingAddressSource = {
  firstName?: string | null;
  lastName?: string | null;
  phone?: string | null;
  phoneNumber?: string | null;
  street?: string | null;
  street2?: string | null;
  city?: string | null;
  provinceCode?: string | null;
  postcode?: string | null;
  countryCode?: string | null;
};

function isEstimatePlaceholderContact(address: ShippingAddressSource): boolean {
  return (
    address.firstName === ESTIMATE_PLACEHOLDER_FIRST_NAME
    && address.lastName === ESTIMATE_PLACEHOLDER_LAST_NAME
  );
}

function isEstimatePlaceholderPhone(phone: string | null | undefined): boolean {
  if (!phone) return true;
  const digits = phone.replace(/\D/g, '');
  return digits === ESTIMATE_PLACEHOLDER_PHONE || digits === '5555555555';
}

function normalizeOrderShippingAddress(
  address: ShippingAddressSource | undefined,
): Partial<Address> | null {
  if (!address?.street || !address.city || !address.postcode) {
    return null;
  }

  const phone = address.phoneNumber ?? address.phone ?? undefined;

  return {
    street: address.street,
    street2: address.street2 ?? '',
    city: address.city,
    provinceCode: address.provinceCode ?? '',
    postcode: address.postcode,
    countryCode: address.countryCode ?? DEFAULT_COUNTRY_CODE,
    firstName: isEstimatePlaceholderContact(address) ? undefined : address.firstName ?? undefined,
    lastName: isEstimatePlaceholderContact(address) ? undefined : address.lastName ?? undefined,
    phoneNumber: isEstimatePlaceholderPhone(phone) ? undefined : phone,
  };
}

/**
 * Builds default shipping field values for checkout from the order (e.g. cart estimate)
 * with session profile address as fallback for contact details.
 */
export function resolveCheckoutShippingDefaults(
  orderData: OrderPayload | null | undefined,
  sessionShippingAddress?: Address | null,
): Partial<Address> {
  const orderAddress = normalizeOrderShippingAddress(orderData?.fulfillment?.shippingAddress);
  const sessionAddress = sessionShippingAddress ?? null;
  const hasOrderShippingAddress = Boolean(orderAddress?.street && orderAddress?.city && orderAddress?.postcode);

  return {
    firstName: orderAddress?.firstName || sessionAddress?.firstName || '',
    lastName: orderAddress?.lastName || sessionAddress?.lastName || '',
    phoneNumber: orderAddress?.phoneNumber || sessionAddress?.phoneNumber || '',
    street: orderAddress?.street || sessionAddress?.street || '',
    street2: orderAddress?.street2 || sessionAddress?.street2 || '',
    city: orderAddress?.city || sessionAddress?.city || '',
    provinceCode: hasOrderShippingAddress
      ? (orderAddress?.provinceCode ?? '')
      : (orderAddress?.provinceCode || sessionAddress?.provinceCode || ''),
    postcode: orderAddress?.postcode || sessionAddress?.postcode || '',
    countryCode: hasOrderShippingAddress
      ? (orderAddress?.countryCode ?? DEFAULT_COUNTRY_CODE)
      : (orderAddress?.countryCode || sessionAddress?.countryCode || DEFAULT_COUNTRY_CODE),
  };
}

export function resolveCheckoutShippingStateCode(
  defaults: Partial<Address>,
  fallback?: string,
): string {
  const countryCode = (defaults.countryCode ?? DEFAULT_COUNTRY_CODE).toUpperCase();
  const resolvedFallback = fallback ?? (isUnitedStates(countryCode) ? 'TX' : '');

  if (!defaults.provinceCode) {
    return resolvedFallback;
  }

  return formatProvinceCodeForDisplay(defaults.countryCode, defaults.provinceCode) || resolvedFallback;
}

export function resolveCheckoutShippingCountryLabel(defaults: Partial<Address>): string {
  return getCountryLabel(defaults.countryCode);
}

export interface EstimateShippingFormDefaults {
  register_address: string;
  register_address2: string;
  register_city: string;
  register_country: string;
  register_state: string;
  register_zipcode: string;
}

const EMPTY_ESTIMATE_SHIPPING_DEFAULTS: EstimateShippingFormDefaults = {
  register_address: '',
  register_address2: '',
  register_city: '',
  register_country: DEFAULT_COUNTRY_CODE,
  register_state: 'TX',
  register_zipcode: '',
};

/**
 * Builds estimate-shipping modal defaults from the account shipping address.
 */
export function resolveEstimateShippingModalDefaults(
  sessionShippingAddress?: Address | null,
): EstimateShippingFormDefaults {
  const normalized = normalizeOrderShippingAddress(sessionShippingAddress ?? undefined);
  if (!normalized?.street || !normalized.city || !normalized.postcode) {
    return EMPTY_ESTIMATE_SHIPPING_DEFAULTS;
  }

  const countryCode = (normalized.countryCode ?? DEFAULT_COUNTRY_CODE).toUpperCase();

  return {
    register_address: normalized.street,
    register_address2: normalized.street2 ?? '',
    register_city: normalized.city,
    register_country: countryCode,
    register_state: resolveCheckoutShippingStateCode(
      normalized,
      isUnitedStates(countryCode) ? 'TX' : '',
    ),
    register_zipcode: normalized.postcode,
  };
}
